"use client"

import Link from "@/lib/navigation"
import { usePathname } from "@/lib/navigation"
import { useEffect, useRef, useState } from "react"
import { House as Home, Scroll as ScrollText, User, Gear as Settings, Users, ShoppingCart, List as Menu, ListChecks, DeviceMobile as Smartphone, Gift, X } from "@phosphor-icons/react"
import { secondaryAdminNavigation, matchesAdminPath, isSecondaryAdminPath } from "./admin-navigation"

const tabs = [
  { path: "/user/dashboard", label: "主页", icon: Home },
  { path: "/user/logs", label: "日志", icon: ScrollText },
  { path: "/user/account", label: "账户", icon: User },
  { path: "/user/settings", label: "设置", icon: Settings },
]

export function BottomTabBar({ role = "user" }: { role?: "user" | "admin" }) {
  const pathname = usePathname()
  const [moreOpen, setMoreOpen] = useState(false)
  const [pendingPath, setPendingPath] = useState<string | null>(null)
  const navRef = useRef<HTMLElement>(null)
  const visibleTabs = role === "admin" ? [
    { path: "/admin/dashboard", label: "总览", icon: Home },
    { path: "/admin/users", label: "用户", icon: Users },
    { path: "/admin/orders", label: "订单", icon: ShoppingCart },
  ] : tabs
  const secondaryIcons: Record<string, typeof Home> = {
    "/admin/tasks": ListChecks,
    "/admin/devices": Smartphone,
    "/admin/cdk": Gift,
    "/admin/logs": ScrollText,
    "/admin/settings": Settings,
  }
  const primaryIndex = visibleTabs.findIndex(tab => matchesAdminPath(pathname || "", tab.path))
  const moreActive = role === "admin" && isSecondaryAdminPath(pathname || "")
  const activeIndex = moreActive ? 3 : Math.max(0, primaryIndex)

  useEffect(() => { setMoreOpen(false); setPendingPath(null) }, [pathname])
  useEffect(() => {
    if (!moreOpen) return
    const close = (event: PointerEvent) => { if (!navRef.current?.contains(event.target as Node)) setMoreOpen(false) }
    document.addEventListener("pointerdown", close)
    return () => document.removeEventListener("pointerdown", close)
  }, [moreOpen])

  return (
    <nav ref={navRef}
      className="user-floating-bottom-nav"
      aria-label={role === "admin" ? "管理员导航" : "用户导航"}
    >
      {role === "admin" && <span className="admin-nav-indicator" style={{ transform: `translateX(${activeIndex * 100}%)` }} aria-hidden="true" />}
      {visibleTabs.map((tab) => {
        const active = pathname === tab.path || pathname?.startsWith(tab.path + "/")
        const Icon = tab.icon
        return (
          <Link
            key={tab.path}
            href={tab.path}
            aria-current={active ? "page" : undefined}
            className={`user-floating-bottom-nav__item${active ? " is-active" : ""}${pendingPath === tab.path ? " is-pressing" : ""}`}
            onClick={() => setPendingPath(tab.path)}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              padding: "7px 0",
              textDecoration: "none",
            }}
          >
            <Icon
              style={{
                width: 20,
                height: 20,
                color: active ? "var(--berry-300)" : "var(--berry-600)",
              }}
            />
            <span
              style={{
                fontSize: 11,
                color: active ? "var(--berry-300)" : "var(--berry-600)",
                fontWeight: active ? 700 : 400,
              }}
            >
              {tab.label}
            </span>
          </Link>
        )
      })}
      {role === "admin" && (
        <button type="button" onClick={() => setMoreOpen(open => !open)} aria-label={moreOpen ? "关闭更多菜单" : "打开更多菜单"} aria-expanded={moreOpen}
          className={`user-floating-bottom-nav__item nav-more${moreActive ? " is-active" : ""}`}>
          {moreOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}<span>更多</span>
        </button>
      )}
      {role === "admin" && moreOpen && <div className="admin-nav-more-panel" aria-label="更多管理模块">
        {secondaryAdminNavigation.map((item: {path:string;label:string}) => {
          const Icon = secondaryIcons[item.path] || Settings
          const active = matchesAdminPath(pathname || "", item.path)
          return <Link key={item.path} href={item.path} aria-current={active ? "page" : undefined} className={active ? "admin-nav-more-item is-active" : "admin-nav-more-item"}>
            <Icon size={20} aria-hidden="true" /><span>{item.label}</span>
          </Link>
        })}
      </div>}
    </nav>
  )
}
